import { isEmpty } from 'utils/string';
import { SubscriptionTypes } from 'utils/subscription';
import { StoreMgmtStates } from './adminTenant';
import { Settings } from './setting';

export const LeadSources_Instant27001 = '7a2561e1-c2f4-496f-8983-360704dd1bce';
export const LeadSources_CAN = '56e4347c-073c-4882-b5b2-9fd9a838c763';
export const LeadSourceInfosentry = '3da37c7b-d723-47b0-a597-5d96c162eec9';
export const LeadSourceACAIT = 'AFDF5148-906A-4AEC-8007-F8C3E044D12C';
export const LeadSourceAfterDark = '17763563-ED29-48F5-8CD3-C307AC1E727F';
export const LeadSourceAR4REAL = '41A9EAF3-EB77-4F4B-8B06-D1BA0EA47D72';
export const LeadSourceBOEM = 'F76AB9A8-1C95-4778-B30C-8F093F899C21';
export const LeadSourceCompleon = '9DB8B5B2-038F-40EB-A4D0-0B2BB3149284';
export const LeadSourceOnyx = '250DBE04-027F-4AAF-B1DD-7BC42470DD56';
export const LeadSourcePisotive = 'ef66b099-ff8d-4f0c-92c4-216133cd87d3';

export const LeadSources: string[] = [
  LeadSources_Instant27001.toLowerCase(),
  LeadSources_CAN.toLowerCase(),
  LeadSourceInfosentry.toLowerCase(),
  LeadSourceACAIT.toLowerCase(),
  LeadSourceAfterDark.toLowerCase(),
  LeadSourceAR4REAL.toLowerCase(),
  LeadSourceBOEM.toLowerCase(),
  LeadSourceCompleon.toLowerCase(),
  LeadSourceOnyx.toLowerCase(),
  LeadSourcePisotive.toLowerCase(),
];

export default class Tenant {
  id: string;

  parentId?: string;

  topLevelParentTenantId: string;

  azureTenantId?: string;

  name: string;

  created?: Date;

  totalLicenses: number;

  usedUserLicenses: number;

  usedMgMtLicenses: number;

  numberOfUsers: number;

  userLicenses: number;

  mgMtLicenses: number;

  storeMgmtState: number;

  onboardingState?: TenantOnboarding;

  subscriptionType: number;

  settings?: Settings;

  timeZoneCode?: string;

  color?: string;

  currentUserLicensed?: boolean;

  appLogo?: Blob;

  constructor(id: string, name: string) {
    this.id = id;
    this.topLevelParentTenantId = id; //default same as id
    this.name = name;
    this.totalLicenses = 0;
    this.usedUserLicenses = 0;
    this.usedMgMtLicenses = 0;
    this.numberOfUsers = 0;
    this.userLicenses = 0;
    this.mgMtLicenses = 0;
    this.storeMgmtState = StoreMgmtStates.Customer;
    this.subscriptionType = SubscriptionTypes.Basic;
  }

  getUsedLicenses(): number {
    return this.usedUserLicenses + this.usedMgMtLicenses;
  }

  getFreeLicenses(): number {
    return this.totalLicenses - this.getUsedLicenses();
  }

  getFreeUserLicenses(): number {
    return this.userLicenses - this.usedUserLicenses;
  }

  getFreeMgMtLicenses(): number {
    return this.mgMtLicenses - this.usedMgMtLicenses;
  }

  static isDescendentOf(child: Tenant, parentId: string | undefined, orgUnits: Tenant[]): boolean {
    if (parentId && child.parentId) {
      if (child.parentId === parentId) return true;
      let nextParent: Tenant | undefined = child;

      nextParent = orgUnits.find((o) => o.id === nextParent?.parentId);
      if (!nextParent) return false;

      return this.isDescendentOf(nextParent, parentId, orgUnits);
    }

    return false;
  }

  clone() {
    const newTenant = new Tenant(this.id, this.name);
    newTenant.parentId = this.parentId;
    newTenant.topLevelParentTenantId = this.topLevelParentTenantId;
    newTenant.azureTenantId = this.azureTenantId;
    newTenant.totalLicenses = this.totalLicenses;
    newTenant.usedUserLicenses = this.usedUserLicenses;
    newTenant.usedMgMtLicenses = this.usedMgMtLicenses;
    newTenant.usedUserLicenses = this.usedUserLicenses;
    newTenant.numberOfUsers = this.numberOfUsers;
    newTenant.userLicenses = this.userLicenses;
    newTenant.mgMtLicenses = this.mgMtLicenses;
    newTenant.storeMgmtState = this.storeMgmtState;
    newTenant.subscriptionType = this.subscriptionType;
    newTenant.timeZoneCode = this.timeZoneCode;
    newTenant.color = this.color;

    return newTenant;
  }
}

//
// On-boarding
//
export enum TrialWelcomeIntent {
  IsFirstISO = 1,
  IsCertified = 2,
  IsConsultant = 3,
}

export class TenantOnboarding {
  packageIds: string[];

  leadSource?: string;

  timeZoneOffset?: number;

  onboardingSteps: TenantOnboardingSteps;

  subscriptionType?: SubscriptionTypes;

  constructor() {
    this.packageIds = [];
    this.onboardingSteps = new TenantOnboardingSteps();
  }

  clone() {
    const newTenantOnboarding = new TenantOnboarding();
    newTenantOnboarding.packageIds = [...this.packageIds];
    newTenantOnboarding.leadSource = this.leadSource;
    newTenantOnboarding.timeZoneOffset = this.timeZoneOffset;
    newTenantOnboarding.onboardingSteps = this.onboardingSteps.clone();
    newTenantOnboarding.subscriptionType = this.subscriptionType;

    return newTenantOnboarding;
  }
}

export class TenantOnboardingSteps {
  finished: Date | undefined;

  currentStep: number | undefined;

  intent: TrialWelcomeIntent | undefined;

  phone: string | undefined;

  languageIsSet: boolean | undefined;

  certifiedStandards: number[] | undefined;

  certifiedOtherStandards: string | undefined;

  newStandards: number[] | undefined;

  newOtherStandards: string | undefined;

  currentSystem: number | undefined;

  currentOtherSystem: string | undefined;

  currentSystemImprovement: string | undefined;

  wantsToCertify: boolean | undefined;

  startDate: Date | undefined;

  endDate: Date | undefined;

  projectDocument: string | undefined;

  dashboardName: string | undefined;

  dashboardWidgetTaskStatusName: string | undefined;

  dashboardWidgetTaskTagName: string | undefined;

  optInAI: boolean = false;

  dashboardId: number | undefined;

  previewPackageInstalled: boolean | undefined;

  projectMilestonesCreated: boolean | undefined;

  isCertified = (): boolean | undefined => {
    if (!this.certifiedStandards) return undefined;

    return this.certifiedStandards.length > 0 || !isEmpty(this.certifiedOtherStandards);
  };

  needNewStandards = (): boolean | undefined => {
    if (!this.newStandards) return undefined;

    return this.newStandards.length > 0 || !isEmpty(this.newOtherStandards);
  };

  clone() {
    const output = new TenantOnboardingSteps();

    output.certifiedStandards = this.certifiedStandards;
    output.certifiedOtherStandards = this.certifiedOtherStandards;
    output.newStandards = this.newStandards;
    output.newOtherStandards = this.newOtherStandards;
    output.wantsToCertify = this.wantsToCertify;
    output.currentSystem = this.currentSystem;
    output.currentOtherSystem = this.currentOtherSystem;
    output.currentSystemImprovement = this.currentSystemImprovement;
    output.startDate = this.startDate;
    output.endDate = this.endDate;
    output.projectDocument = this.projectDocument;
    output.dashboardName = this.dashboardName;
    output.dashboardWidgetTaskStatusName = this.dashboardWidgetTaskStatusName;
    output.dashboardWidgetTaskTagName = this.dashboardWidgetTaskTagName;
    output.languageIsSet = this.languageIsSet;
    output.optInAI = this.optInAI;
    output.dashboardId = this.dashboardId;
    output.previewPackageInstalled = this.previewPackageInstalled;
    output.projectMilestonesCreated = this.projectMilestonesCreated;
    output.currentStep = this.currentStep;
    output.intent = this.intent;
    output.phone = this.phone;
    output.finished = this.finished;

    return output;
  }
}
